import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../../services/authService';
import type { RootState } from '../../app/store';
import { normalizeRoles, tokenScope, hasPermission } from '../../rbac/rbac.generated';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'system-admin' | 'tenant-admin';
  roles?: string[]; // full role list from auth-service (admin is also an employee)
  tenantId?: string;
  tenantName?: string;
  permissions: string[];
  avatar?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Async thunks
export const loginWithToken = createAsyncThunk(
  'auth/loginWithToken',
  async (token: string) => {
    const response = await authService.validateToken(token);
    return response;
  }
);

export const loginWithCredentials = createAsyncThunk(
  'auth/loginWithCredentials',
  async (
    creds: { email: string; password: string; workspace: string },
    { rejectWithValue },
  ) => {
    try {
      return await authService.login(creds.email, creds.password, creds.workspace);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || 'Login failed',
      );
    }
  },
);

// P2-8: restore the session on load from the httpOnly cookie (via /session),
// replacing the old "decode the stored admin_token" path.
export const hydrateSession = createAsyncThunk(
  'auth/hydrate',
  async (_: void, { rejectWithValue }) => {
    const user = await authService.hydrateSession();
    if (!user) return rejectWithValue('no session');
    return { user: user as unknown as User, token: '' };
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout();
});

export const refreshToken = createAsyncThunk('auth/refresh', async () => {
  const response = await authService.refreshToken();
  return response;
});

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Login with token
    builder
      .addCase(loginWithToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(loginWithToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Authentication failed';
        state.isAuthenticated = false;
      });

    // P2-8 cookie hydrate on load
    builder
      .addCase(hydrateSession.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(hydrateSession.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });

    // Credentials login
    builder
      .addCase(loginWithCredentials.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithCredentials.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user as User;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(loginWithCredentials.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Login failed';
        state.isAuthenticated = false;
      });

    // Logout
    builder
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });

    // Refresh token
    builder
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });
  },
});

export const { setCredentials, clearAuth, setError } = authSlice.actions;

// Selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
// Admin-level roles. Tolerant of the admin-portal convention
// (system-admin / tenant-admin), the canonical RBAC roles (platform_* /
// tenant_owner — Model A: admin-portal is the platform console), and the legacy
// auth-service JWT names (system_admin / tenant_admin / admin).
const ADMIN_ROLES = [
  'system-admin',
  'tenant-admin',
  'system_admin',
  'tenant_admin',
  'admin',
  'platform_owner',
  'platform_admin',
  'platform_support',
  'platform_billing',
  'platform_analyst',
  'tenant_owner',
];
export const selectIsAdmin = (state: RootState) => {
  const user = state.auth.user as { role?: string; roles?: string[] } | null;
  if (!user) return false;
  const roles = Array.isArray(user.roles) ? user.roles : user.role ? [user.role] : [];
  return roles.some((r) => ADMIN_ROLES.includes(r));
};
export const selectIsSystemAdmin = (state: RootState) =>
  ['system-admin', 'system_admin'].includes(state.auth.user?.role as string);

// --- Canonical RBAC selectors (Phase 2) ---
const rawRolesOf = (u: { roles?: string[]; role?: string } | null): string[] =>
  Array.isArray(u?.roles) && u!.roles!.length ? u!.roles! : u?.role ? [u.role] : [];

/** The user's canonical (alias-normalized) roles. */
export const selectRoles = (state: RootState) => normalizeRoles(rawRolesOf(state.auth.user));
/** Token-level scope derived from the user's roles. */
export const selectScope = (state: RootState) => tokenScope(selectRoles(state));
/** admin-portal is the operator console — only platform-scoped users may enter. */
export const selectIsPlatform = (state: RootState) => selectScope(state) === 'platform';
/** Curried permission selector: useAppSelector(selectHasPermission('tenant:read:all')). */
export const selectHasPermission =
  (permission: string) => (state: RootState) =>
    hasPermission(selectRoles(state), permission);

export default authSlice.reducer;